import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import type { OrderedResponseContentBlock } from "../../types";
import { QuestionHeader } from "./question-header";
import { useInteractiveSubmit } from "../../hooks/useInteractiveSubmit";
import {
  interactiveRootSx,
  useInteractiveStyles,
} from "./styles";
import { useShuffledItems } from "../../hooks/useShuffledItems";
import type { AssessmentInteractiveModeProps } from "./assessment-mode";
import { notifyOfflineActionBlocked } from "../../utils/internetConnection";

const normalize = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

interface OrderedResponseBlockProps extends AssessmentInteractiveModeProps {
  block: OrderedResponseContentBlock;
  blockIndex: number;
  messageId: string;
  userId: string;
  tabId: string;
  onQuizAnswered?: () => void;
}

export const OrderedResponseBlock: React.FC<OrderedResponseBlockProps> = ({
  block,
  blockIndex,
  messageId,
  userId,
  tabId,
  onQuizAnswered,
  hideSubmitButton = false,
  hideFeedback = false,
  hideResultHighlight = false,
  isAssessmentMode = false,
  submitGetterRef,
  onCompletionChange,
}) => {
  const interactiveStyles = useInteractiveStyles(isAssessmentMode);
  const disabled = block.state === "disable";
  const revealResults = disabled && !hideResultHighlight;
  const slotCount = Math.max(1, block.slot_count);
  const { submit, isSubmitting, hasSubmitted, isBrowserLocked, isOffline } =
    useInteractiveSubmit(onQuizAnswered);
  const isInteractionLocked =
    isSubmitting || hasSubmitted || isBrowserLocked || isOffline;

  const [draftOrder, setDraftOrder] = useState<(string | null)[]>(() => {
    if (block.user_order?.length) {
      const slots = Array.from(
        { length: slotCount },
        () => null as string | null,
      );
      block.user_order.forEach((id, i) => {
        if (i < slotCount) slots[i] = id;
      });
      return slots;
    }
    return Array.from({ length: slotCount }, () => null);
  });

  useEffect(() => {
    const slots = Array.from(
      { length: slotCount },
      () => null as string | null,
    );
    block.user_order?.forEach((id, i) => {
      if (i < slotCount) slots[i] = id;
    });
    setDraftOrder(slots);
  }, [block.user_order, block.state, slotCount, tabId]);

  const slots = disabled
    ? Array.from(
        { length: slotCount },
        (_, i) => block.user_order?.[i] ?? null,
      )
    : draftOrder;

  const optionById = useMemo(
    () => new Map(block.options.map((o) => [o.id, o])),
    [block.options],
  );

  const shuffledOptions = useShuffledItems(
    block.options,
    messageId,
    blockIndex,
    tabId,
  );

  const usedInSlots = useMemo(
    () => new Set(slots.filter(Boolean) as string[]),
    [slots],
  );

  const poolOptions = useMemo(() => {
    if (disabled) return [];
    return shuffledOptions.filter((o) => !usedInSlots.has(o.id));
  }, [shuffledOptions, disabled, usedInSlots]);

  const allFilled = slots.every(Boolean);
  const isSubmitDisabled = !allFilled || isInteractionLocked;

  useEffect(() => {
    onCompletionChange?.(allFilled);
  }, [allFilled, onCompletionChange]);

  useEffect(() => {
    if (!submitGetterRef) return;
    submitGetterRef.current = () => {
      if (!allFilled) return null;
      return {
        interactionType: "ordered_response",
        payload: { user_order: slots.filter(Boolean) as string[] },
      };
    };
    return () => {
      submitGetterRef.current = null;
    };
  }, [submitGetterRef, allFilled, slots]);

  const expectedOrderFromAnswers = block.answers ?? [];

  const getSlotHighlight = (slotIndex: number): "correct" | "incorrect" | undefined => {
    if (!revealResults) return undefined;
    const expectedRaw = expectedOrderFromAnswers[slotIndex];
    const actualId = slots[slotIndex];
    if (!expectedRaw || !actualId) return undefined;

    const expectedOption = optionById.get(expectedRaw);
    const actualOption = optionById.get(actualId);
    const expected = normalize(expectedOption?.id ?? expectedRaw);
    const actualIdNorm = normalize(actualId);
    const actualTextNorm = normalize(actualOption?.text);

    return actualIdNorm === expected || actualTextNorm === expected
      ? "correct"
      : "incorrect";
  };

  const addFromPool = useCallback(
    (optionId: string) => {
      if (disabled || hasSubmitted || isBrowserLocked) return;
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      setDraftOrder((prev) => {
        const emptyIndex = prev.findIndex((id) => !id);
        if (emptyIndex < 0) return prev;
        const next = [...prev];
        next[emptyIndex] = optionId;
        return next;
      });
    },
    [disabled, hasSubmitted, isBrowserLocked, isOffline],
  );

  const removeFromSlot = useCallback(
    (index: number) => {
      if (disabled || hasSubmitted || isBrowserLocked) return;
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      setDraftOrder((prev) => {
        const next = [...prev];
        next[index] = null;
        return next;
      });
    },
    [disabled, hasSubmitted, isBrowserLocked, isOffline],
  );

  const moveUp = useCallback(
    (index: number) => {
      if (disabled || hasSubmitted || isBrowserLocked || index <= 0) return;
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      setDraftOrder((prev) => {
        const next = [...prev];
        [next[index - 1], next[index]] = [next[index], next[index - 1]];
        return next;
      });
    },
    [disabled, hasSubmitted, isBrowserLocked, isOffline],
  );

  const moveDown = useCallback(
    (index: number) => {
      if (
        disabled ||
        hasSubmitted ||
        isBrowserLocked ||
        index >= slotCount - 1
      ) {
        return;
      }
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      setDraftOrder((prev) => {
        const next = [...prev];
        [next[index], next[index + 1]] = [next[index + 1], next[index]];
        return next;
      });
    },
    [disabled, hasSubmitted, isBrowserLocked, isOffline, slotCount],
  );

  const clearAll = useCallback(() => {
    if (disabled || hasSubmitted || isBrowserLocked) return;
    if (isOffline) {
      notifyOfflineActionBlocked();
      return;
    }
    setDraftOrder(Array.from({ length: slotCount }, () => null));
  }, [disabled, hasSubmitted, isBrowserLocked, isOffline, slotCount]);

  const handleSubmit = async () => {
    if (!allFilled || disabled || hideSubmitButton || isInteractionLocked) {
      return;
    }
    await submit({
      messageId,
      userId,
      tabId,
      interactionType: "ordered_response",
      blockIndex,
      data: block.data,
      payload: { user_order: slots.filter(Boolean) as string[] },
    });
  };

  return (
    <Box sx={interactiveRootSx}>
      <QuestionHeader
        title={block.title}
        instruction={block.instruction}
        prompt={block.prompt}
        styles={interactiveStyles}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography sx={interactiveStyles.poolLabelSx}>Your response</Typography>
        {!disabled && usedInSlots.size > 0 ? (
          <Button
            variant="text"
            size="small"
            onClick={clearAll}
            disabled={isInteractionLocked}
            sx={interactiveStyles.slotActionButtonSx}
          >
            Clear all
          </Button>
        ) : null}
      </Box>

      <Box
        sx={{
          ...interactiveStyles.cardSx,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {slots.map((optionId, index) => {
          const text = optionId ? optionById.get(optionId)?.text : null;
          return (
            <Box
              key={`slot-${index}`}
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: interactiveStyles.palette.purple,
                  minWidth: 20,
                  fontSize: "1rem",
                  pt: { xs: 1, sm: 0 },
                }}
              >
                {index + 1}
              </Typography>
              <Box
                sx={interactiveStyles.orderedResponseSlotSx(
                  !!text,
                  getSlotHighlight(index),
                )}
              >
                {text ?? (disabled ? "—" : "Select a block below")}
              </Box>
              {!disabled && text ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 0.25,
                    flexShrink: 0,
                  }}
                >
                  <Button
                    variant="text"
                    size="small"
                    disabled={index === 0 || isInteractionLocked}
                    onClick={() => moveUp(index)}
                    sx={interactiveStyles.slotActionButtonSx}
                  >
                    Up
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    disabled={index === slotCount - 1 || isInteractionLocked}
                    onClick={() => moveDown(index)}
                    sx={interactiveStyles.slotActionButtonSx}
                  >
                    Down
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    disabled={isInteractionLocked}
                    onClick={() => removeFromSlot(index)}
                    sx={interactiveStyles.slotActionButtonSx}
                  >
                    Remove
                  </Button>
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>

      {!disabled ? (
        <Box>
          <Typography sx={interactiveStyles.poolLabelSx}>
            Available response blocks
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {poolOptions.map((option) => (
              <Box
                key={option.id}
                onClick={() => {
                  if (isInteractionLocked || allFilled) return;
                  if (isOffline) {
                    notifyOfflineActionBlocked();
                    return;
                  }
                  addFromPool(option.id);
                }}
                sx={{
                  ...interactiveStyles.getOptionChipSx(false),
                  display: "flex",
                  width: "100%",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  cursor:
                    allFilled || isInteractionLocked
                      ? "not-allowed"
                      : "pointer",
                  opacity: allFilled || isInteractionLocked ? 0.5 : 1,
                  whiteSpace: "normal",
                  lineHeight: 1.4,
                }}
              >
                {option.text}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      {block.feedback && disabled && !hideFeedback ? (
        <Typography sx={{ fontSize: "0.875rem", color: "#374151" }}>
          {block.feedback}
        </Typography>
      ) : null}

      {!disabled && !hideSubmitButton ? (
        <Button
          variant="contained"
          disableElevation
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          sx={interactiveStyles.submitButtonSx(isSubmitDisabled)}
        >
          {isSubmitting ? "Submitting..." : "Submit Answer"}
        </Button>
      ) : null}
    </Box>
  );
};
